<template>
  <div class="flex space-x-3 relative">
    <div class="text-xl font-medium mb-7">
      {{ $t("Select Visible Pages") }}
    </div>

    <div v-if="editing_disabled == 'all'" class="w-10 pl-2">
      <LoadingSpinner />
    </div>

    <span v-if="workspace_layout.filter((p) => !p.is_visible).length && !editing_disabled">
      <button class="btn cancel-btn" @click="selectAllPages">{{ $t("Select All") }}</button>
    </span>

    <span v-if="workspace_layout.filter((p) => p.is_visible).length > 1 && !editing_disabled">
      <button class="btn cancel-btn" @click="unselectAllPages">{{ $t("Unselect All") }}</button>
    </span>

    <button
      class="btn absolute -mt-2 right-72 cancel-btn m-2 py-4"
      @click="$router.push(`/workspace/${workspace.short_id}/${workspace.layout.filter((l) => l.is_visible)[0].id}?last_page=setup`)">
      {{ $t("View and customize pages") }}
    </button>
  </div>

  <draggable
    v-model="workspace_layout"
    handle=".drag-handle"
    filter=".ignore-elements"
    :component-data="{ type: 'transition-group', name: !drag ? 'flip-list' : null }"
    v-bind="dragOptions"
    item-key="name"
    class="border-light-divider-border w-2/3 mt-4 dark:border-dark-divider-border max-w-full text-left transition ease-in-out duration-side-menu-transition-duration"
    tag="tr"
    @start="drag = true"
    @end="
      () => {
        drag = false
        $store.dispatch('data/updateWorkspace', { short_id: workspace?.short_id, layout: workspace_layout })
      }
    ">
    <template #item="{ element, index }">
      <div
        class="flex relative py-5 justify-between items-center"
        :class="[{ 'border-b': index < workspace.layout.length - 1 }]"
        @mouseenter="page_hovered = element.id"
        @mouseleave="page_hovered = null">
        <button v-if="page_hovered == element.id && !page_to_edit" class="button absolute drag-handle hover:cursor-move">
          <Tally2 class="h-5 w-5 text-common-grey rotate-90" aria-hidden="true" />
        </button>

        <div v-if="page_to_edit?.id == element.id">
          <div class="flex space-x-3 items-center">
            <div>
              <div class="w-[300px]">
                <TextInput
                  :value="page_to_edit.name"
                  :validate-on-input="true"
                  label=""
                  :classes="'w-[200px]'"
                  input-classes="appearance-none block w-[200px] mb-4 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                  name="name"
                  type="text"
                  :placeholder="$t('Page Name')"
                  @update:modelValue="
                    ($event) => {
                      new_page_name = $event
                    }
                  " />
              </div>
              <TextInput
                :value="page_to_edit.description"
                :validate-on-input="true"
                label=""
                :classes="'w-full'"
                input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
                name="name"
                type="text"
                :placeholder="$t('Page Description')"
                @update:modelValue="
                  ($event) => {
                    new_page_description = $event
                  }
                " />
            </div>
            <div v-if="new_page_name !== element.name || new_page_description !== element.description" @click="handleEditPage">
              <CircleCheck class="text-green-500 cursor-pointer w-7 h-7" />
            </div>
            <div @click="page_to_edit = null">
              <CircleX class="text-common-grey cursor-pointer w-7 h-7" />
            </div>
          </div>
        </div>

        <div v-else class="ml-10">
          <div class="font-medium text-lg mb-1">
            {{ element.name }}

            <button
              v-if="page_hovered == element.id && element.custom_page && !page_to_edit"
              :class="[{ 'mt-4': element.description }]"
              class="button ml-8 absolute mt-1">
              <Pencil
                class="h-5 w-5 text-common-grey"
                @click="
                  () => {
                    page_to_edit = element
                    new_page_name = element.name
                    new_page_description = element.description
                  }
                " />
            </button>

            <button
              v-if="page_hovered == element.id && element.custom_page && !page_to_edit"
              :class="[{ 'mt-4': element.description }]"
              class="button ml-16 absolute mt-1">
              <Trash2 class="h-5 w-5 text-common-grey" @click="handleDeleteWorkspace(element)" />
            </button>
          </div>
          <div class="text-common-grey">
            {{ element.description }}
          </div>
        </div>
        <div class="relative">
          <Switch
            v-if="!editing_disabled || editing_disabled !== element.id"
            :disabled="editing_disabled"
            :value="element.is_visible"
            :class="[
              element.is_visible ? 'bg-blue' : 'bg-gray-300',
              ' relative inline-flex flex-shrink-0 h-5 w-10 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue'
            ]"
            @update:modelValue="(e) => handleSwitchClick(!element.is_visible, element)">
            <span
              aria-hidden="true"
              :class="[
                element.is_visible && $store.state.portfolio.ui_options?.language == 'ar'
                  ? '-translate-x-5'
                  : element.is_visible
                    ? 'translate-x-5'
                    : 'translate-x-0',
                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
              ]" />
          </Switch>

          <div v-else class="w-10 pl-2">
            <LoadingSpinner />
          </div>
          <button
            v-if="(!editing_disabled || editing_disabled !== element.id) && element.is_visible"
            class="ml-6 absolute btn cancel-btn -mt-3"
            @click="$router.push(`/workspace/${workspace.short_id}/${element.id}?last_page=setup`)">
            {{ $t("Edit Page") }}
          </button>
        </div>
      </div>
    </template>
  </draggable>

  <div v-if="add_new_page_active">
    <div class="w-[300px]">
      <TextInput
        :validate-on-input="true"
        label=""
        :classes="'w-[200px]'"
        input-classes="appearance-none block w-[200px] mb-4 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
        name="name"
        type="text"
        :placeholder="$t('Page Name')"
        @update:modelValue="
          ($event) => {
            new_page_name = $event
          }
        " />
    </div>
    <TextInput
      :validate-on-input="true"
      label=""
      :classes="'w-full'"
      input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
      name="name"
      type="text"
      :placeholder="$t('Page Description')"
      @update:modelValue="
        ($event) => {
          new_page_description = $event
        }
      " />
  </div>

  <div v-if="add_new_page_active" class="flex space-x-4 mt-4">
    <button
      v-if="new_page_name && !workspace_layout.filter((l) => l.name.toLowerCase() == new_page_name.toLowerCase()).length"
      class="btn primary-btn w-32"
      @click="handleAddNewPage">
      {{ $t("Add") }}
    </button>
    <button id="cancel-btn" class="btn cancel-btn w-32" @click="add_new_page_active = false">{{ $t("Cancel") }}</button>
  </div>

  <button v-if="!add_new_page_active" class="btn cancel-btn" @click="handleAddNewPageClick">{{ $t("Add New Custom Page") }}</button>
</template>

<script>
import TextInput from "@/components/shared/TextInput.vue"
import { Switch } from "@headlessui/vue"
import draggable from "vuedraggable"
import LoadingSpinner from "@/components/shared/LoadingSpinner.vue"
import { Tally2, Trash2, CircleCheck, Pencil, CircleX } from "lucide-vue-next"
import { v4 as uuidv4 } from "uuid"

import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: { LoadingSpinner, Switch, draggable, Tally2, Trash2, TextInput, Pencil, CircleCheck, CircleX },
  props: { workspace: null },
  emits: [],
  data() {
    return {
      drag: false,
      workspace_layout: [],
      page_hovered: null,
      add_new_page_active: false,
      new_page_name: "",
      new_page_description: "",
      page_to_edit: null,
      dont_trigger: false,
      editing_disabled: false
    }
  },

  computed: {
    dragOptions() {
      return {
        animation: 200,
        group: "user header",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },

  watch: {
    add_new_page_active() {
      this.new_page_name = ""
      this.new_page_description = ""
    },
    // workspace_layout: {
    //   deep: true,
    //   handler() {
    //     if (!this.dont_trigger) {
    //       this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: this.workspace_layout })
    //     }
    //   }
    // },
    workspace: {
      deep: true,
      handler() {
        this.workspace_layout = this.$utils?.clone(this.workspace.layout)
      }
    }
  },

  created() {
    this.register_listeners()
    this.dont_trigger = true
    setTimeout(() => {
      this.dont_trigger = false
    }, 400)
    this.workspace_layout = this.$utils.clone(this.workspace.layout)
  },
  activated() {
    this.register_listeners()

    this.dont_trigger = true
    setTimeout(() => {
      this.dont_trigger = false
    }, 400)
    this.workspace_layout = this.$utils.clone(this.workspace.layout)
  },
  deactivated() {
    this.unregister_listeners()
  },
  unmounted() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.WORKSPACE_UPDATED_IN_PAGES, () => {
        this.workspace_layout = this.$utils?.clone(this.workspace.layout)
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.WORKSPACE_UPDATED_IN_PAGES)
    },
    handleAddNewPageClick() {
      this.add_new_page_active = true
      setTimeout(() => {
        document.getElementById("cancel-btn").scrollIntoView()
      }, 300)
    },
    handleDeleteWorkspace(element) {
      this.$emitter.emit(ALL_EMITTERS.REMOVE_WORKSPACE_PAGE, {
        page: element,
        workspace: { ...this.workspace, layout: this.workspace_layout }
      })
    },
    handleEditPage() {
      const index = this.workspace_layout.findIndex((l) => l.id == this.page_to_edit.id)
      this.workspace_layout[index].name = this.new_page_name
      this.workspace_layout[index].description = this.new_page_description
      this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: this.workspace_layout })
      this.page_to_edit = false
    },
    handleAddNewPage() {
      const new_page_obj = {
        id: uuidv4(),
        name: this.new_page_name,
        layout: [],
        is_static: true,
        description: this.new_page_description,
        is_visible: true,
        custom_page: true
      }

      const new_layout = this.$utils.clone(this.workspace_layout)
      new_layout.splice(this.workspace_layout.filter((l) => l.is_visible).length, 0, new_page_obj)
      this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: new_layout }).then((res) => {
        this.add_new_page_active = false
        this.workspace_layout = this.$utils.clone(res.data.layout)
      })
    },
    handleSwitchClick(e, page) {
      const layout_copy = this.$utils.clone(this.workspace_layout)

      if (layout_copy.filter((p) => p.is_visible == true).length == 1 && !e) {
        this.$toast.open({
          message: this.$t("At least one page has to be checked"),
          position: "top",
          type: "error",
          duration: 3000
        })
      } else {
        let index = layout_copy.findIndex((p) => page.name == p.name)
        this.editing_disabled = page.id

        const new_index = layout_copy.filter((l) => l.is_visible).length
        layout_copy.splice(index, 1)
        layout_copy.splice(e ? new_index : new_index - 1, 0, { ...page, is_visible: e })
      }
      this.workspace_layout = layout_copy
      this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: this.workspace_layout }).then(() => {
        setTimeout(() => {
          this.editing_disabled = false
        }, 50)
      })
    },

    selectAllPages() {
      const layout_copy = this.$utils.clone(this.workspace_layout)
      layout_copy.forEach((_, i) => {
        layout_copy.splice(i, 1, { ...layout_copy[i], is_visible: true })
      })
      this.editing_disabled = "all"
      this.workspace_layout = layout_copy
      this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: this.workspace_layout }).then(() => {
        setTimeout(() => {
          this.editing_disabled = false
        }, 50)
      })
    },

    unselectAllPages() {
      const layout_copy = this.$utils.clone(this.workspace_layout)
      layout_copy.forEach((_, i) => {
        layout_copy.splice(i, 1, { ...layout_copy[i], is_visible: false })
      })
      layout_copy.splice(0, 1, { ...layout_copy[0], is_visible: true })
      this.workspace_layout = layout_copy
      this.editing_disabled = "all"
      this.$store.dispatch("data/updateWorkspace", { short_id: this.workspace?.short_id, layout: this.workspace_layout }).then(() => {
        setTimeout(() => {
          this.editing_disabled = false
        }, 50)
      })
    }
  }
}
</script>
